// W3.5 — public landing page (beta open, Aug 2026). The marketing-first face of
// littlebankers.uk for visitors who aren't in a household and haven't passed the invite
// gate: what the app is, a taste of what's inside, "we're in beta", and two CTAs —
// join the waitlist, or "I've got a code" which reveals the EXISTING InvitationGate
// (Root owns that switch; this component never re-implements the gate). Joined devices
// and invite-pass holders never see this page — Root's inviteOk check is unchanged.
//
// W1 journey-review pass (Alan, 27 Aug): screenshots strip ("peek inside", review 1.2),
// explicit you're-the-bank body (1.4/P1, no competitor named), pricing-signal banner
// (1.6/P2: paid plans WILL come one day + free tier is permanent — both stated, no
// grandfather promises), two-question mini-FAQ, trust block with support email (1.3 +
// 5.4), age claim 5–12 (1.5 — Sprout/Reception ladder stages untouched, only the claim
// moves), waitlist cadence line + resend affordance (1.7). Data-location wording stays
// SOFT ("hosted on Cloudflare") until DPIA Q-D11 confirms a pinned jurisdiction — do not
// harden to "UK/EU" without that answer.
//
// Waitlist spam posture (agreed 26 Aug): Cloudflare Turnstile (managed, invisible for
// nearly all humans) + honeypot field + worker-side IP rate limit + double opt-in via
// Resend (unconfirmed entries carry a KV TTL and expire; only confirmed addresses
// count). See worker/handlers/waitlist.js for the server half. The resend button reuses
// the exact same join() path — the worker treats a repeat post as a fresh pending +
// fresh email (already-confirmed addresses come back {already:true}), and a resend
// spends from the same IP window as a join, which is the correct anti-abuse posture.
//
// Turnstile degrades safely in both directions: if the script fails to load (blocked,
// offline, CI), the form still submits and the WORKER decides — when TURNSTILE_SECRET
// is configured a missing token is rejected (no client-side bypass hole), when it
// isn't (local dev) the submission is accepted unverified. The widget is lazy-loaded
// only when this page actually renders, so app users never fetch a third-party script.
//
// Marketing screenshots load lazily from /marketing/ and are deliberately NOT in the
// service-worker precache: they exist only for this pre-household page, and precaching
// would push ~350KB into every joined device's cache for a screen those devices never
// see again. The landing is an online-only surface by nature (its whole job is a
// network POST), so the offline-correctness rationale behind the precache rule doesn't
// apply — recorded as a deliberate deviation in the W1 brief.
//
// All copy via ct() with baked fallbacks (cms-seed/landing.json) — Alan's post-launch
// edits need no deploy.

import { CopyrightFooter, Icon, LittleBankersLogo, SafeAreaSpacer } from "../components/Common.jsx";
import { TURNSTILE_SITE_KEY } from "../lib/constants.js";
import { t as ct } from "../lib/content.js";
import { apiUrl } from "../lib/origin.js";
import { getAcquisition, setMetricsScreen, track } from "../lib/metrics.js";
const { useEffect, useRef, useState } = React;

const TURNSTILE_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function useTurnstile(containerRef) {
  const [widgetId, setWidgetId] = useState(null);
  useEffect(() => {
    let cancelled = false;
    function render() {
      if (cancelled || !containerRef.current || !window.turnstile) return;
      try {
        const id = window.turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          appearance: "interaction-only", // invisible unless a challenge is actually needed
        });
        setWidgetId(id);
      } catch {}
    }
    if (window.turnstile) render();
    else {
      let script = document.querySelector(`script[src="${TURNSTILE_SRC}"]`);
      if (!script) {
        script = document.createElement("script");
        script.src = TURNSTILE_SRC;
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", render);
      // No error handler needed: submit simply proceeds without a token and the worker
      // rules on it (see header comment).
    }
    return () => { cancelled = true; };
  }, []);
  return () => {
    try { return widgetId !== null && window.turnstile ? window.turnstile.getResponse(widgetId) || "" : ""; } catch { return ""; }
  };
}

// Horizontal pillar (Alan, design pass 26 Aug): sprite icon + title stacked on a fixed
// left rail, body on the right — three of these fit above the fold where the stacked
// version pushed the CTA below it. Icons are the app's own sprite (Common.jsx Icon /
// little-bankers-icons.svg), same ids the features use in-app, so the landing promise
// and the first-run UI literally share artwork.
function Pillar({ icon, hue, title, body }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 px-3 py-2.5 flex items-center gap-3">
      <div className="w-14 shrink-0 flex flex-col items-center gap-0.5">
        <Icon id={icon} size={26} hue={hue} />
        <div className="text-xs font-bold text-slate-900">{title}</div>
      </div>
      <p className="text-xs text-slate-500 leading-relaxed flex-1">{body}</p>
    </div>
  );
}

// W1 review 1.2, carousel form (Alan, 27 Aug): a swipe carousel with the centred shot
// in focus and its neighbours smaller and softened, plus tap-to-enlarge. Scroll-snap
// does the swiping natively; a rAF-throttled scroll listener measures each slide's
// distance from the strip's centre and maps it to scale/opacity/blur — no carousel
// library, no state churn per frame (styles are written to the DOM directly). The
// lightbox is a plain fixed overlay: tap anywhere or press Escape to close; the
// trigger is a real <button> so keyboard/screen-reader users get it for free.
function PeekStrip() {
  // Eight slides (Alan, 10 Sep: "add more images — parent mode and more kid mode, even if
  // there is repetition"). Shots are cropped from the hero down, no app chrome, ending on a
  // block boundary, so nothing is cut mid-content.
  const shots = [
    { src: "/marketing/kid-earn.png", alt: "Chore and habit tracker for kids: today's tasks ticked off, a streak, and a list of jobs a child can claim tokens for", caption: ct("landing.peek.cap_earn", "Earn through daily tasks and other activities") },
    { src: "/marketing/kid-learn.png", alt: "Learning games that pay pocket money: spelling, vocabulary, maths and times tables, with a spelling test ready to start", caption: ct("landing.peek.cap_learn", "English and Maths tests pay in tokens") },
    { src: "/marketing/kid-home-young.png", alt: "The app for a five-year-old: bigger type, simpler blocks and three things to do today", caption: ct("landing.peek.cap_young", "At five") },
    { src: "/marketing/kid-home.png", alt: "The app for an eleven-year-old: token balance, Savings Vault, today's question and the daily habit streak", caption: ct("landing.peek.cap_home", "At eleven") },
    { src: "/marketing/coach-summary.png", alt: "Parent view of learning progress: four subjects, each with the child's level, tests last week and a plain-English note on how it is going", caption: ct("landing.peek.cap_coach_summary", "See what is clicking, and what is not") },
    { src: "/marketing/kid-goals.png", alt: "Saving towards goals: a child's wish list with progress bars and one goal ready to redeem", caption: ct("landing.peek.cap_goals", "View progress towards their goals") },
    { src: "/marketing/kid-vault.png", alt: "The Savings Vault paying interest every Money Monday, so a child watches compound interest happen", caption: ct("landing.peek.cap_vault", "Money Monday makes saving pay") },
    { src: "/marketing/kid-piggington.png", alt: "Piggington the pig with the child's Oinks and the Oink Shop, where characters are unlocked", caption: ct("landing.peek.cap_piggington", "Piggington and the Oink Shop") },
    { src: "/marketing/parent-home.png", alt: "Parent Mode home: a request waiting for approval, quick actions, and what a token is worth in this household", caption: ct("landing.peek.cap_parent_home", "Your view of the whole family") },
    { src: "/marketing/parent-requests.png", alt: "Approving pocket money: a child's request to earn tokens for helping with dinner, with Approve and Reject", caption: ct("landing.peek.cap_requests", "You approve every spend") },
  ];
  // Prev / next (Alan, 10 Sep: no manual buttons on a laptop). Scroll by one slide; the
  // scroll-snap and the focus effect do the rest. Hidden below md, where a thumb does this.
  function nudge(dir) {
    const strip = stripRef.current; if (!strip) return;
    const slide = strip.querySelector("figure"); const step = slide ? slide.getBoundingClientRect().width + 12 : 260;
    strip.scrollBy({ left: dir * step, behavior: "smooth" });
  }
  const stripRef = useRef(null);
  const [atStart, setAtStart] = useState(true);
  const [lightbox, setLightbox] = useState(null); // index | null

  // Focus effect: centre slide at full scale, neighbours scale down, dim and blur with
  // distance. Runs on scroll (rAF-throttled) and once on mount; also on resize since
  // the centre point moves.
  useEffect(() => {
    const strip = stripRef.current;
    if (!strip) return;
    let raf = 0;
    function paint() {
      raf = 0;
      setAtStart(strip.scrollLeft < 8); // the back arrow only appears once they have moved
      // getBoundingClientRect(), not offsetLeft (Alan, 27 Aug ×4 — the actual root cause
      // of the dullness, not the falloff numbers): offsetLeft is relative to the slide's
      // offsetParent, the nearest POSITIONED ancestor — which is nowhere near this strip
      // (nothing between here and the page root is position:relative/absolute), so it
      // was picking up ~40px of unrelated outer-page offset on every measurement. That
      // meant the "focused" slide never actually measured as centred, so it always
      // carried a bit of residual dim/desaturate — worse the harder take 3 pushed the
      // falloff. Bounding rects are viewport-relative for both strip and slide, so their
      // difference is correct regardless of scroll position or ancestor positioning.
      const stripRect = strip.getBoundingClientRect();
      const mid = stripRect.left + stripRect.width / 2;
      for (const slide of strip.children) {
        const slideRect = slide.getBoundingClientRect();
        const centre = slideRect.left + slideRect.width / 2;
        // Dead zone (Alan, 27 Aug ×3): the focused slide must be EXACTLY full-scale and
        // fully opaque — any residual dim reads as "out of focus". Distances under 24px
        // count as centred (scroll-snap rests within a couple of px, but never assume
        // exactly 0).
        const raw = Math.abs(centre - mid);
        const t = raw < 24 ? 0 : Math.min(1, (raw - 24) / strip.clientWidth);
        const img = slide.querySelector("img");
        if (!img) continue;
        // Take 3: v2's falloff (scale ×0.1, opacity ×0.3) left neighbours barely
        // different from the centre, so nothing read as "in focus" — just uniformly
        // flat. Steeper falloff plus a desaturate+darken filter (no blur — still ruled
        // out, see below) makes the centre pop by contrast instead of relying on scale
        // alone; a lift shadow on the centre slide adds the last bit of "this one's up
        // front" depth.
        img.style.transform = `scale(${1 - 0.16 * t})`;
        img.style.opacity = String(1 - 0.45 * t);
        img.style.filter = t === 0 ? "none" : `saturate(${1 - 0.5 * t}) brightness(${1 - 0.15 * t})`;
        img.style.boxShadow = t === 0 ? "0 8px 24px -8px rgba(15, 23, 42, 0.35)" : "none";
        // No blur (Alan, 27 Aug): the px-16 gutters can't centre a slide perfectly in
        // this card width, so t never quite reaches 0 and even the focused shot kept a
        // residual blur — every image read as out of focus. Scale+dim carry the focus
        // effect cleanly on their own.
      }
    }
    function onScroll() { if (!raf) raf = requestAnimationFrame(paint); }
    paint();
    strip.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      strip.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Escape closes the lightbox — same convention as PinPad's key handling.
  useEffect(() => {
    if (lightbox === null) return;
    function onKey(e) { if (e.key === "Escape") setLightbox(null); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <div className="mb-4" data-testid="peek-strip">
      <h2 className="text-xs font-bold text-slate-900 mb-2 text-center">{ct("landing.peek.heading", "Take a peek inside")}</h2>
      {/* Gutters are CALCULATED, not guessed (the v1 px-16 guess is why every shot kept
          residual dim: the first/last slide could never reach dead-centre, so t never
          hit 0). calc(50% - half the slide width) means every slide, edges included,
          can rest exactly centred — which the dead zone in the paint fn then rewards
          with full scale and opacity. Slides upsized w-44 → w-56 (Alan, 27 Aug). */}
      <div className="relative">
      <button type="button" data-testid="peek-prev" aria-label={ct("landing.peek.prev", "Previous screenshot")} onClick={() => nudge(-1)} className={`${atStart ? "hidden" : "hidden md:flex"} absolute left-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white border border-slate-200 shadow items-center justify-center text-slate-700`}><Icon id="ic-back" size={18} style={{ color: "currentColor" }} /></button>
      <button type="button" data-testid="peek-next" aria-label={ct("landing.peek.next", "Next screenshot")} onClick={() => nudge(1)} className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white border border-slate-200 shadow items-center justify-center text-slate-700"><Icon id="ic-forward" size={18} style={{ color: "currentColor" }} /></button>
      <div
        ref={stripRef}
        className="flex gap-3 overflow-x-auto pb-1"
        style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none", paddingInline: "calc(50% - 7rem)" }}
      >
        <figure className="shrink-0 w-56 md:w-64 m-0" style={{ scrollSnapAlign: "center" }} data-testid="peek-howto">
          <div className="w-56 md:w-64 h-[340px] bg-white rounded-lg border border-slate-200 p-4 flex flex-col">
            <h3 className="text-[15px] font-extrabold text-slate-900 mb-2">{ct("landing.peek.howto_title", "How it works")}</h3>
            <ul className="list-none p-0 m-0 space-y-2.5">
              {[
                ct("landing.peek.howto_1", "Each child has **their own profile**, on your phone, their tablet or a laptop."),
                ct("landing.peek.howto_2", "Kids are **free to roam** in Kid Mode, but the settings and decisions are **behind your PIN**."),
                ct("landing.peek.howto_3", "Kids **choose how to earn and spend**, inside the limits you set. **You stay in control**."),
                ct("landing.peek.howto_4", "Set a **weekly cap** to control costs; rewards are **sized to fit it** and keep it interesting."),
              ].map((line) => (
                <li key={line} className="text-[12.5px] leading-snug text-slate-600 flex gap-2">
                  <span className="text-indigo-600 font-extrabold flex-shrink-0" aria-hidden="true">&#8226;</span>
                  {/* **bold** lives in the copy so the CMS decides what is emphasised: odd
                      segments of the split are the bold ones. */}
                  <span>{line.split("**").map((part, n) => (n % 2 ? <b key={n} className="font-bold text-slate-900">{part}</b> : part))}</span>
                </li>
              ))}
            </ul>
          </div>
          <figcaption className="text-[13px] text-slate-600 mt-2 text-center">{ct("landing.peek.cap_howto", "How it works")}</figcaption>
        </figure>
        {shots.map((s, i) => (
          <figure key={s.src} className="shrink-0 w-56 md:w-64 m-0" style={{ scrollSnapAlign: "center" }}>
            <button
              type="button"
              className="block w-56 md:w-64 p-0 border-0 bg-transparent cursor-zoom-in"
              aria-label={ct("landing.peek.enlarge", "Enlarge screenshot: {caption}", { caption: s.caption })}
              data-testid={`peek-shot-${i}`}
              onClick={() => setLightbox(i)}
            >
              <img
                src={s.src}
                alt={s.alt}
                loading="lazy"
                className="w-56 md:w-64 h-[340px] object-contain object-top bg-slate-100 p-2 rounded-lg border border-slate-200 block transition-transform duration-150"
              />
            </button>
            <figcaption className="text-xs text-slate-400 text-center mt-1 font-medium">{s.caption}</figcaption>
          </figure>
        ))}
      </div>
      </div>
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/90 flex flex-col items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-label={shots[lightbox].caption}
          data-testid="peek-lightbox"
          onClick={() => setLightbox(null)}
        >
          <img src={shots[lightbox].src} alt={shots[lightbox].alt} className="max-h-[80vh] max-w-full rounded-lg shadow-2xl" />
          <p className="text-white/80 text-xs font-semibold mt-3">{shots[lightbox].caption}</p>
          <p className="text-white/50 text-xs mt-1">{ct("landing.peek.close_hint", "Tap anywhere to close")}</p>
        </div>
      )}
    </div>
  );
}

// W1 review 1.4 + 1.6 — the two questions every prospective parent actually asks, right
// on the page instead of behind the gate (the in-app Help FAQ isn't reachable from
// here). Native <details>: zero JS, accessible for free, and closed by default so the
// page stays short for people who don't need them.
function MiniFaq() {
  const items = [
    { q: ct("landing.faq.card_q", "Is this a debit card for kids?"), a: ct("landing.faq.card_a", "No. Card apps give your child a card and hold real money. Little Bankers holds none: it's the family ledger, and you stay the bank. Nothing to top up, nothing locked away, no fees possible.") },
    { q: ct("landing.faq.free_q", "Will it stay free?"), a: ct("landing.faq.free_a", "The Preview is completely free. Later we plan optional paid extras for families who want more, but the core app will always have a free tier.") },
  ];
  return (
    <div className="mb-4" data-testid="landing-faq">
      {items.map((it) => (
        <details key={it.q} className="bg-white rounded-lg border border-slate-200 px-3.5 py-2.5 mb-2 group">
          <summary className="text-xs font-bold text-slate-800 cursor-pointer list-none flex items-center justify-between gap-2">
            {it.q}
            <Icon id="ic-forward" size={14} className="text-slate-900 flex-shrink-0 transition-transform group-open:rotate-90" style={{ color: "currentColor" }} />
          </summary>
          <p className="text-xs text-slate-500 leading-relaxed mt-1.5">{it.a}</p>
        </details>
      ))}
    </div>
  );
}

// LB-92 (Alan, 9 Sep 2026): on a laptop the landing is a page designed for the width, not
// the phone stack enlarged — top bar with the code-holder route and the CTA, split hero with
// the screenshot carousel beside the words and the waitlist form itself, a numbered
// how-it-works band, Earn / Save / Learn each with a full screenshot, a Money Monday feature
// block, the bank + trust statement as two columns, the FAQ, a repeat CTA. Same claims,
// same copy keys, same screenshots; below 1024px the phone page renders untouched.
function useWide() {
  const [wide, setWide] = useState(() => typeof window !== "undefined" && window.matchMedia && window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const on = () => setWide(mq.matches);
    mq.addEventListener ? mq.addEventListener("change", on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener("change", on) : mq.removeListener(on); };
  }, []);
  return wide;
}

function WaitlistFormWide({ f, compact, hidePitch, idSuffix = "" }) {
  // Same state, same handlers, same testids as the phone form — one form per page, so
  // the ids stay unique. `compact` drops the pitch line for the foot-of-page repeat.
  if (f.done) {
    // Alan, 10 Sep: the acknowledgement was too subtle — a tinted panel with a check now.
    return (
      <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3.5 max-w-md" data-testid={`waitlist-check-email${idSuffix}`}>
        <div className="flex items-start gap-2.5">
          <span className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 mt-0.5"><Icon id="ic-check" size={14} style={{ color: "currentColor" }} /></span>
          <div>
            <div className="font-extrabold text-slate-900 mb-0.5">{f.resent ? ct("landing.waitlist.resent_title", "Sent again") : f.already ? ct("landing.waitlist.already_title", "That address is already on the list") : ct("landing.waitlist.done_title", "You're nearly on the list")}</div>
            <div className="text-sm text-slate-700">{f.resent ? ct("landing.waitlist.resent", "Sent again — give it a minute, and check your spam folder too.") : f.already ? ct("landing.waitlist.already_body", "We've sent the confirmation email again in case the first one went astray. Nothing else to do.") : ct("landing.waitlist.check_email", "One more step: we've sent you an email. Click the link in it to confirm your spot. If it hasn't arrived, check your spam folder.")}</div>
          </div>
        </div>
        <button className="text-sm font-bold text-slate-900 underline underline-offset-2 mt-2.5 ml-[34px]" data-testid={`waitlist-resend${idSuffix}`} disabled={f.busy} onClick={() => f.join(true, idSuffix)}>
          {f.busy ? ct("landing.waitlist.resending", "Sending…") : ct("landing.waitlist.resend", "Didn't get the email? Send it again")}
        </button>
      </div>
    );
  }
  return (
    <div>
      {!compact && !hidePitch && <p className="text-sm text-slate-600 mb-3">{ct("landing.waitlist.pitch", "We're letting families in wave by wave — roughly fortnightly. Join the waitlist and your invite code lands by email.")}</p>}
      <div className="flex gap-2 max-w-md">
        <input ref={f.emailRef} type="email" inputMode="email" autoComplete="email" data-testid={`waitlist-email${idSuffix}`} value={f.email} onChange={(e) => f.setEmail(e.target.value)} placeholder={compact ? ct("landing.waitlist.email_placeholder_short", "Email address") : ct("landing.waitlist.email_placeholder", "Your email address")}
          className="flex-1 min-w-0 border border-slate-200 rounded-lg px-3.5 py-3 text-[15px] bg-white" onKeyDown={(e) => { if (e.key === "Enter") f.join(false, idSuffix); }} />
        <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => f.setWebsite(e.target.value)} className="hidden" aria-hidden="true" />
        <button onClick={() => f.join(false, idSuffix)} disabled={f.busy} className="bg-navy text-white font-bold rounded-lg px-5 py-3 text-[15px] whitespace-nowrap">
          {f.busy ? ct("landing.waitlist.joining", "Joining…") : compact ? ct("landing.waitlist.cta_short", "Join") : ct("landing.waitlist.cta", "Join the waitlist")}
        </button>
      </div>
      <div ref={f.turnstileRef} className="mt-2" />
      {f.error && f.errorAt === idSuffix && (
        <div className="mt-2 rounded-lg border border-rose-300 bg-rose-50 px-3.5 py-2.5 text-sm font-semibold text-rose-800 max-w-md flex items-start gap-2" role="alert"><Icon id="ic-attention" size={16} className="flex-shrink-0 mt-0.5" style={{ color: "currentColor" }} />{f.error}</div>
      )}
      <p className="text-xs text-slate-500 mt-2 max-w-md">{ct("landing.waitlist.consent", "By joining, you're happy for us to email your invite code and occasional Preview updates. Unsubscribe anytime.")}</p>
    </div>
  );
}

// LB-361 (Alan, 22 Sep; phone and desktop drawings signed off the same day). A shared invite
// link used to open a bare code screen (LB-94), which gave someone following a friend's
// WhatsApp link no idea what they had been sent. It now opens THIS page, with every
// "join the waitlist" ask replaced by the code they already hold and one Get started.
//
// `invite` comes from Root: { code, onCodeChange, onSubmit, busy, error }. Get started is
// the ONLY thing that validates, and validating spends a use (worker/lib/invites.js), so
// nothing here submits on arrival, on render or on focus. If the code fails, the error
// shows where they tapped and the waitlist comes back (onWaitlist), because the waitlist
// is the one thing that can get them a working code.
function InviteBox({ invite, where, onWaitlist }) {
  const go = () => { if (!invite.busy && invite.code.trim()) { track("landing_cta", { kind: `invite_get_started_${where}` }); invite.onSubmit(); } };
  return (
    <section aria-labelledby={`landing-invite-heading-${where}`} className="bg-amber-50 border border-amber-300 rounded-lg p-4 flex flex-col gap-2.5 text-left" data-testid={`landing-invite-${where}`}>
      <h2 id={`landing-invite-heading-${where}`} className="text-base font-extrabold text-slate-900 text-center m-0">{ct("landing.invite.heading", "You're invited")}</h2>
      <p className="text-xs text-slate-600 text-center m-0">{ct("landing.invite.body", "Your invitation code is ready. Get started to set up your household.")}</p>
      {invite.error && <div role="alert" className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2">{invite.error}</div>}
      <label htmlFor={`landing-invite-code-${where}`} className="text-xs font-bold text-slate-600">{ct("landing.invite.code_label", "Invitation code")}</label>
      <input
        id={`landing-invite-code-${where}`}
        data-testid={`landing-invite-code-${where}`}
        className={`h-11 border ${invite.error ? "border-rose-300" : "border-slate-200"} rounded-lg px-3 text-sm w-full text-center tracking-widest font-bold uppercase bg-white`}
        value={invite.code}
        autoComplete="off"
        onChange={(e) => invite.onCodeChange(e.target.value.toUpperCase())}
        onKeyDown={(e) => e.key === "Enter" && go()}
      />
      <button type="button" disabled={invite.busy || !invite.code.trim()} className="h-11 w-full bg-slate-900 text-white font-bold text-sm rounded-lg disabled:opacity-50" data-testid={`landing-invite-go-${where}`} onClick={go}>
        {invite.busy ? ct("landing.invite.checking", "Checking…") : ct("landing.invite.cta", "Get started")}
      </button>
      {invite.error && (
        <button type="button" className="text-xs font-bold text-slate-900 underline underline-offset-2 text-center" data-testid={`landing-invite-waitlist-${where}`} onClick={onWaitlist}>
          {ct("onboarding.invite.code_not_working", "Code not working? Join the waitlist")}
        </button>
      )}
    </section>
  );
}

// The desktop version: the band's own shape (the words on the left, the field on the right),
// so the page reads as it always has with one ask swapped for another.
function InviteBandWide({ invite, where, heading, onWaitlist, onSignIn }) {
  const go = () => { if (!invite.busy && invite.code.trim()) { track("landing_cta", { kind: `invite_get_started_${where}` }); invite.onSubmit(); } };
  return (
    <div className="grid grid-cols-[1.05fr_.95fr] gap-12 items-start" data-testid={`landing-invite-${where}`}>
      <div>
        <h2 id={`landing-invite-heading-${where}`} className="text-2xl font-extrabold text-slate-900 mb-3">{heading}</h2>
        <p className="text-slate-600 text-[17px] max-w-[46ch] m-0">{ct("landing.invite.body", "Your invitation code is ready. Get started to set up your household.")}</p>
      </div>
      <section aria-labelledby={`landing-invite-heading-${where}`} className="flex flex-col gap-2.5">
        <label htmlFor={`landing-invite-code-${where}`} className="text-[13px] font-bold text-slate-600">{ct("landing.invite.code_label", "Invitation code")}</label>
        <div className="flex gap-2 max-w-md">
          <input
            id={`landing-invite-code-${where}`}
            data-testid={`landing-invite-code-${where}`}
            className={`flex-1 min-w-0 border ${invite.error ? "border-rose-300" : "border-slate-200"} rounded-lg px-3.5 py-3 text-base bg-white text-center tracking-widest font-bold uppercase`}
            value={invite.code}
            autoComplete="off"
            onChange={(e) => invite.onCodeChange(e.target.value.toUpperCase())}
            onKeyDown={(e) => e.key === "Enter" && go()}
          />
          <button type="button" disabled={invite.busy || !invite.code.trim()} className="bg-navy text-white font-bold rounded-lg px-5 py-3 text-[15px] whitespace-nowrap disabled:opacity-50" data-testid={`landing-invite-go-${where}`} onClick={go}>
            {invite.busy ? ct("landing.invite.checking", "Checking…") : ct("landing.invite.cta", "Get started")}
          </button>
        </div>
        {invite.error && (
          <div role="alert" className="rounded-lg border border-rose-300 bg-rose-50 px-3.5 py-2.5 text-sm font-semibold text-rose-800 max-w-md flex items-start gap-2"><Icon id="ic-attention" size={16} className="flex-shrink-0 mt-0.5" style={{ color: "currentColor" }} />{invite.error}</div>
        )}
        <p className="text-sm font-bold text-slate-700 mt-1 flex flex-wrap gap-x-4 gap-y-1">
          {invite.error && <button className="underline underline-offset-2" data-testid={`landing-invite-waitlist-${where}`} onClick={onWaitlist}>{ct("onboarding.invite.code_not_working", "Code not working? Join the waitlist")}</button>}
          <button className="underline underline-offset-2" onClick={() => { track("landing_cta", { kind: "sign_in" }); onSignIn(); }}>{ct("landing.sign_in", "Already have a household? Sign in with your code")}</button>
        </p>
      </section>
    </div>
  );
}

function LandingWide({ f, onHaveCode, onSignIn, waitlistConfirmed, invite, invited, onWaitlist }) {
  const steps = [
    [ct("landing.how.step1_title", "You set what everything's worth"), ct("landing.how.step1_body", "One token can be 10p, or whatever works for your family. You can set different rewards for each child, to suit their age and personality.")],
    [ct("landing.how.step2_title", "Kids earn, learn and save"), ct("landing.how.step2_body", "Kids move closer to their goals as they earn tokens from daily tasks, activities, and learning games matched to their age and ability.")],
    [ct("landing.how.step3_title", "You approve every spend"), ct("landing.how.step3_body", "You stay in control. You set the weekly spending cap and approve every request, whether they're earning or spending.")],
  ];
  const cols = [
    { icon: "ic-medal", hue: "gold", title: ct("landing.pillar.earn_title", "Earn"), body: ct("landing.pillar.earn_body", "Tokens for daily habits, jobs around the house and good behaviour — with streaks that keep it going."), src: "/marketing/kid-earn.png", alt: "Kid Earn screen: daily tasks and Claim your earnings" },
    { icon: "ic-supersaver", hue: "teal", title: ct("landing.pillar.save_title", "Save"), body: ct("landing.pillar.save_body", "Real goals, a Savings Vault, and Money Monday interest that shows saving pays."), src: "/marketing/kid-goals.png", alt: "Kid Rewards screen: goals with one ready to redeem" },
    { icon: "ic-brain", hue: "indigo", title: ct("landing.pillar.learn_title", "Learn"), body: ct("landing.pillar.learn_body", "Spelling, vocabulary, maths and times tables for ages 5 to 11, which adapt to your child's ability and are designed to challenge them."), src: "/marketing/coach-subject.png", alt: "Learn & Earn: four games and a spelling test ready to start" },
  ];
  return (
    <div className="min-h-screen" data-testid="landing-wide">
      <div className="max-w-[1120px] mx-auto px-8">
        <nav className="flex items-center justify-between pt-6 pb-1">
          <LittleBankersLogo className="h-24" />{/* larger lockup on the laptop page (Alan, 10 Sep) */}
          <div className="flex items-center gap-5">
            {/* LB-361: someone who arrived holding a code is not asked if they have one. */}
            {!invite && <button className="text-sm font-bold text-slate-800 underline underline-offset-2" data-testid="landing-have-code-hero" onClick={() => { track("landing_cta", { kind: "have_code_hero" }); onHaveCode(); }}>{ct("landing.hero_have_code", "Got an invite code? Enter it here")}</button>}
            {invited
              // Same act as the band's button (Alan, 22 Sep): it checks the code in the box.
              ? <button className="bg-navy text-white font-bold rounded-lg px-5 py-2.5 text-sm disabled:opacity-50" data-testid="landing-invite-go-nav" disabled={invite.busy || !invite.code.trim()} onClick={() => { track("landing_cta", { kind: "invite_get_started_nav" }); invite.onSubmit(); }}>{invite.busy ? ct("landing.invite.checking", "Checking…") : ct("landing.invite.cta", "Get started")}</button>
              : <button className="bg-navy text-white font-bold rounded-lg px-5 py-2.5 text-sm" onClick={() => { track("landing_cta", { kind: "nav_waitlist" }); f.emailRef.current?.focus(); f.emailRef.current?.scrollIntoView({ block: "center" }); }}>{ct("landing.nav.cta", "Get an invite")}</button>}
          </div>
        </nav>
        {waitlistConfirmed && <div className="border-t border-b border-slate-200 text-slate-700 text-sm font-semibold py-3 mb-4 text-center" data-testid="waitlist-confirmed-banner">{ct("landing.waitlist.confirmed_banner", "You're on the list. We'll email your invite code when the next wave opens.")}</div>}
        <section className="grid grid-cols-[1.05fr_.95fr] gap-12 items-start pt-2 pb-4">
          <div>
            <span className="inline-block text-xs font-bold bg-amber-100 text-amber-700 rounded px-2 py-0.5 mb-4" data-testid="beta-pill">{ct("landing.beta_pill", "Invite-only Preview")}</span>
            <h1 className="text-[48px] leading-[1.05] font-extrabold text-slate-900 tracking-tight mb-4 max-w-[18ch]">{ct("landing.hero.heading", "Where kids learn the value of money")}</h1>
            <p className="text-[19px] text-slate-700 font-semibold mb-4 max-w-[46ch]" data-testid="landing-problem">{ct("landing.hero.problem", "We all want our kids to learn to manage money well, but pocket money for nothing doesn't teach them anything. So, you do the right thing and link reward to effort, and suddenly you're the administrator: notes on the fridge, a jar of plastic tokens, a tally in your phone and, of course, the weekly argument.")}</p>
            <p className="text-[19px] text-slate-600 mb-6 max-w-[46ch]">{ct("landing.hero.subheading", "Little Bankers brings helping out, learning, and saving together in one simple app for the whole family. Kids earn tokens, build towards goals, and watch their savings grow. You set what everything's worth and approve every request. We take care of the admin.")}</p>
            {/* 12 Sep (Alan): under the promise it qualifies, in the same column, rather
                than as a footnote to the form or a band across the page. */}
            <p className="text-[15px] text-slate-600 mb-6 max-w-[46ch]" data-testid="youre-the-bank"><b className="text-slate-900">{ct("landing.youre_the_bank_title", "You're the bank")}.</b> {ct("landing.youre_the_bank", "Little Bankers isn't a card or a bank account — no real money is stored anywhere. It's a family money tracker you run: you set what tokens are worth, and you approve every spend.")}</p>
            

          </div>
          <div>
            <div className="relative h-[380px] mt-[86px]" aria-hidden="true">
              <img src="/marketing/parent-requests.png" alt="" className="absolute left-[250px] top-[90px] w-[300px] rounded-[24px] border border-slate-200 shadow-xl" loading="lazy" />
              <img src="/marketing/kid-home.png" alt="" className="absolute left-10 top-0 w-[270px] rounded-[24px] border border-slate-200 shadow-xl" loading="lazy" />
            </div>
          </div>
        </section>
        {/* 12 Sep (Alan): tops aligned, and the ask centred beneath both columns rather than
            hanging off one of them. */}
        {/* 12 Sep (Alan): a title, the line and the field on one baseline, and the two
            links under the field rather than stranded on the left. */}
        <div className="pb-12" data-testid="waitlist-band">
          {invited ? <InviteBandWide invite={invite} where="band" heading={ct("landing.invite.heading", "You're invited")} onWaitlist={onWaitlist} onSignIn={onSignIn} /> : (
          <div className="grid grid-cols-[1.05fr_.95fr] gap-12 items-start">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900 mb-3">{ct("landing.waitlist.heading", "How do I get started?")}</h2>
              <p className="text-slate-600 text-[17px] max-w-[46ch] m-0">{ct("landing.waitlist.pitch", "We're letting families in wave by wave — roughly fortnightly. Join the waitlist and your invite code lands by email.")}</p>
            </div>
            <div>
              <WaitlistFormWide f={f} hidePitch />
              <p className="text-sm font-bold text-slate-700 mt-3 flex flex-wrap gap-x-4 gap-y-1">
                <button className="underline underline-offset-2" onClick={() => { track("landing_cta", { kind: "have_code_bottom" }); onHaveCode(); }}>{ct("landing.have_code", "I've got a code")}</button>
                <button className="underline underline-offset-2" onClick={() => { track("landing_cta", { kind: "sign_in" }); onSignIn(); }}>{ct("landing.sign_in", "Already have a household? Sign in with your code")}</button>
              </p>
            </div>
          </div>
          )}
        </div>
      </div>
      <div className="bg-navy text-white">
        <div className="max-w-[1120px] mx-auto px-8 grid grid-cols-3 gap-8 py-9" data-testid="landing-how">
          {steps.map(([t, b], i) => (
            <div key={i} className="grid grid-cols-[36px_1fr] gap-3.5 items-start">
              <div className="w-9 h-9 rounded-full flex items-center justify-center font-extrabold" style={{ background: "var(--marigold, #FFC629)", color: "var(--navy, #1B2A4A)" }}>{i + 1}</div>
              <div><div className="font-extrabold text-[17px] mb-0.5">{t}</div><div className="text-sm" style={{ color: "#C8D0E0" }}>{b}</div></div>
            </div>
          ))}
        </div>
      </div>
      <div className="max-w-[1120px] mx-auto px-8">
        <section className="pt-12 pb-2"><PeekStrip /></section>
        <h2 className="text-[19px] font-bold text-slate-900 pt-10 -mb-4">{ct("landing.pillars.lead", "Effort earns it. Saving grows it. Learning pays for it.")}</h2>
        <section className="grid grid-cols-3 gap-6 pt-10 pb-5 items-stretch">
          {cols.map((c) => (
            <div key={c.title} className="flex flex-col">
              <h3 className="text-xl font-extrabold text-slate-900 flex items-center gap-2.5 mb-1.5"><Icon id={c.icon} hue={c.hue} size={30} />{c.title}</h3>
              <p className="text-[15px] text-slate-600 mb-3.5 min-h-[72px]">{c.body}</p>
              {/* One height and one crop for all three, so the row reads as a row. */}
              <img src={c.src} alt={c.alt} className="mt-auto w-full h-[470px] object-contain object-top bg-slate-100 p-3 rounded-2xl border border-slate-200" loading="lazy" />
            </div>
          ))}
        </section>
        <section className="grid grid-cols-[1.15fr_.85fr] gap-10 items-center py-12">
          <div>
            <div className="text-[13px] font-bold text-teal-600 mb-1.5">{ct("landing.mm.kicker", "Money Monday")}</div>
            <h2 className="text-3xl font-extrabold text-slate-900 leading-tight mb-3">{ct("landing.mm.title", "Saving pays, and every Monday they see it")}</h2>
            <p className="text-slate-600 max-w-[58ch] mb-3">{ct("landing.mm.question", "Want to teach your kids about savings and interest?")}</p>
            <p className="text-slate-600 max-w-[58ch] mb-3">{ct("landing.mm.body", "Turn on Vault interest and let them experience it. You set an interest rate and every Monday we add the interest to their Savings Vault, so next week earns on last week's interest. Compound interest, explained by watching it happen.")}</p>
            <p className="text-slate-600 max-w-[58ch] mb-3">{ct("landing.mm.body3", "Tokens outside the vault earn nothing, just like most bank accounts, so kids have a reason to move tokens in rather than leave them sitting there. It teaches them to balance everyday spending and saving from a young age.")}</p>
            <p className="text-slate-800 font-semibold max-w-[58ch]">{ct("landing.mm.bank", "You're still the bank. You set the rate, and you settle up in real money on your terms. The interest shows in their Savings Vault, but no real money moves inside the app.")}</p>
          </div>
          <div className="h-[420px] overflow-hidden rounded-2xl border border-slate-200 justify-self-center w-full max-w-[320px] bg-slate-50 p-3">
            <img src="/marketing/kid-vault.png" alt="Kid Rewards screen: the Savings Vault paying interest every Money Monday" className="w-full h-full object-contain object-top" loading="lazy" />
          </div>
        </section>
        {/* LB-92 (Alan, drawings signed off 12 Sep): three columns of roughly equal weight —
            the words, what YOU see, what THEY see — with the bullets as a full-width band
            beneath rather than a panel in a narrow column, where every line wrapped. The two
            screens are cut to an identical canvas, so top alignment starts them on the same
            line whatever their length. */}
        <section className="pt-2 pb-10" data-testid="landing-adaptive">
          <div className="text-[13px] font-bold text-indigo-700 mb-2.5 flex items-center gap-2">{ct("landing.adaptive.kicker", "Learning Coach")}<span className="text-[10px] font-extrabold uppercase tracking-wide bg-amber-100 text-amber-800 rounded px-1.5 py-0.5">{ct("adaptive.row.premium", "Premium")}</span><span className="text-slate-500 font-semibold">{ct("adaptive.row.beta", "free in the Preview")}</span></div>
          <div className="grid grid-cols-[1fr_.74fr_.74fr] gap-6 items-start">
            {/* The copy starts at the top, level with the kicker; the screens start there too. */}
            <div className="self-start">
              <h2 className="text-3xl font-extrabold text-slate-900 leading-tight mb-5">{ct("landing.adaptive.title", "The right level, for each child and each subject")}</h2>
              <p className="text-slate-600 leading-relaxed max-w-[52ch] mb-5">{ct("landing.adaptive.body", "No two children are the same, and no child is the same across all subjects. Being quick at maths doesn't make spelling easy. The level of difficulty matters more than you'd think: too hard and a child feels demoralised and gives up; too easy and they're not learning anything.")}</p>
              <p className="text-slate-600 leading-relaxed max-w-[52ch] mb-5">{ct("landing.adaptive.body2", "You're welcome to set every word list and difficulty level yourself. Or let Learning Coach do it: it finds your child's level in each subject and sets questions that challenge them without overwhelming them. Then it keeps pace as they change. When they're flying, it moves them up. When they're struggling, it eases off and works the weak spots until they stick.")}</p>
              <p className="text-slate-600 leading-relaxed max-w-[52ch]">{ct("landing.adaptive.body3", "You stay in control: take over any subject whenever you like, and Learning Coach steps back for that one until you hand it over again.")}</p>
            </div>
            <figure className="m-0">
              <div className="overflow-hidden rounded-2xl">
                <img src="/marketing/coach-summary.png" alt="Coach's summary: four games, each with its level, tests last week and a plain-English insight" className="w-full block rounded-2xl" loading="lazy" />
              </div>
              <figcaption className="text-center text-[13px] text-slate-500 mt-2">{ct("landing.adaptive.cap_parent", "What you see")}</figcaption>
            </figure>
            <figure className="m-0">
              <div className="overflow-hidden rounded-2xl">
                <img src="/marketing/coach-kid-learn.png" alt="The child's Learn tab: what is going well, each game's level, and a test in progress" className="w-full block rounded-2xl" loading="lazy" />
              </div>
              <figcaption className="text-center text-[13px] text-slate-500 mt-2">{ct("landing.adaptive.cap_kid", "What they see")}</figcaption>
            </figure>
          </div>
          <div className="bg-indigo-50 border-y border-indigo-100 mt-7 py-4 px-6 -mx-6">
            <ul className="max-w-[900px] mx-auto list-none p-0 m-0 grid grid-cols-2 gap-x-8 gap-y-2">
              {[
                ct("landing.adaptive.point2", "Moves them up when earned, eases off when needed"),
                ct("landing.adaptive.point3", "Levels are never lost, so a bad week costs nothing"),
                ct("landing.adaptive.point4", "A weekly summary, so you know without asking"),
                ct("landing.adaptive.point5", "Take over any game, at any time"),
              ].map((line) => (
                <li key={line} className="text-[15px] text-slate-700 flex items-baseline gap-2 whitespace-nowrap">
                  <span className="text-indigo-600 font-extrabold flex-shrink-0" aria-hidden="true">&#10003;</span>{line}
                </li>
              ))}
            </ul>
          </div>
        </section>
        <div className="bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-lg px-6 py-5 grid grid-cols-2 gap-8 mb-10" data-testid="free-banner">
          <div><b className="block text-lg text-slate-900 mb-1">{ct("landing.free_banner", "All features are free during the Preview")}</b><p className="text-[15px] text-slate-600 m-0">{ct("landing.free_banner_sub", "All features, including premium ones, are free to use during the Preview. Don't worry, there will always be a fantastic free product for you to use.")}</p></div>
          <div data-testid="trust-block"><b className="block text-lg text-slate-900 mb-1">{ct("landing.trust_title", "Made for families")}</b><p className="text-[15px] text-slate-600 m-0">{ct("landing.trust.line1", "Designed and built in the UK by parents, for families to use. Your data is not sold or shared, no ads are shown to kids, and you are in total control.")}</p></div>
        </div>
        <MiniFaq />
        {/* Same shape as the get-started band: heading and line on the left, the form on
            the right with the consent note and both code links beneath it. */}
        <div className="px-1 py-6 mb-8" data-testid="foot-waitlist">
          {invited ? <InviteBandWide invite={invite} where="foot" heading={ct("landing.foot.title", "Ready when you are")} onWaitlist={onWaitlist} onSignIn={onSignIn} /> : (
          <div className="grid grid-cols-[1.05fr_.95fr] gap-12 items-start">
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900 mb-2">{ct("landing.foot.title", "Ready when you are")}</h2>
              <p className="text-slate-600 text-[17px] max-w-[46ch] m-0">{ct("landing.waitlist.cadence", "Waves open roughly fortnightly — your invite code lands by email when yours does.")}</p>
            </div>
            <div>
              <WaitlistFormWide f={{ ...f, emailRef: { current: null }, turnstileRef: { current: null } }} hidePitch idSuffix="-foot" />
            </div>
          </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-6 pb-10 text-sm">
          {!invite && <button className="font-bold text-slate-900 underline underline-offset-2" data-testid="landing-have-code" onClick={() => { track("landing_cta", { kind: "have_code" }); onHaveCode(); }}>{ct("landing.have_code_cta", "I've got a code")}</button>}
          <button className="font-bold text-slate-900 underline underline-offset-2" data-testid="landing-sign-in" onClick={() => { track("landing_cta", { kind: "sign_in" }); onSignIn(); }}>{ct("landing.sign_in_cta", "Already have a household? Sign in with your code")}</button>
        </div>
        <CopyrightFooter />
      </div>
    </div>
  );
}

export function LandingPage({ onHaveCode, onSignIn, waitlistConfirmed, focusWaitlist, invite = null }) {
  // LB-361: arrived on a shared invite link. `invited` is the page with every waitlist ask
  // swapped for the code; it drops back to the ordinary waitlist only when the code has
  // failed and they choose "Code not working? Join the waitlist".
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const invited = !!invite && !waitlistOpen;
  const [email, setEmail] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — humans never see or fill this
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState("");
  const turnstileRef = useRef(null);
  const emailRef = useRef(null);
  const getToken = useTurnstile(turnstileRef);
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  useEffect(() => {
    setMetricsScreen("landing");
    track("landing_view", {});
  }, []);

  // LB-95 — arriving here from the invite gate or the join screen, the signup form is
  // most of a page below the fold, behind a pitch this person has already been sent past.
  // Scroll it into view and focus the field so the next thing they do is type an email.
  // Focus is deliberate rather than scroll-only: it also announces the destination to a
  // screen reader, which a silent scroll would not. Guarded on the ref because the form
  // is absent in the `done` branch, where there is nothing left to fill in.
  useEffect(() => {
    if (!focusWaitlist) return;
    const el = emailRef.current;
    if (!el) return;
    try {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.focus({ preventScroll: true });
    } catch {
      try { el.focus(); } catch {}
    }
  }, [focusWaitlist]);

  // LB-361: the waitlist coming back after a failed code, in view and focused, the same way
  // LB-95 brings it into view from the code screens.
  useEffect(() => {
    if (!waitlistOpen) return;
    const el = emailRef.current;
    if (!el) return;
    try { el.scrollIntoView({ behavior: "smooth", block: "center" }); el.focus({ preventScroll: true }); } catch { try { el.focus(); } catch {} }
  }, [waitlistOpen]);
  function openWaitlist() { track("landing_cta", { kind: "waitlist_from_invite" }); setWaitlistOpen(true); }

  const [errorAt, setErrorAt] = useState("");
  const [already, setAlready] = useState(false); // the worker says the address was already on the list (Alan, 10 Sep: say so)
  async function join(isResend, at = "") {
    setErrorAt(at);
    if (!emailOk) { setError(ct("landing.waitlist.email_error", "Enter a valid email address.")); return; }
    setBusy(true); setError("");
    track("landing_cta", { kind: isResend ? "waitlist_resend" : "waitlist" });
    try {
      const acq = getAcquisition();
      const res = await fetch(apiUrl("/api/waitlist"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          token: getToken(),
          website, // honeypot rides along; the worker silently drops filled ones
          betaUpdates: true, // consent surfaced by the line under the CTA; worker stores it with a timestamp
          ...(acq ? { source: acq.source, medium: acq.medium, campaign: acq.campaign } : {}),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Couldn't join the waitlist right now — try again in a minute.");
      setDone(true);
      setAlready(!!data.already);
      if (isResend) setResent(true);
      else track("waitlist_joined", {});
    } catch (err) {
      setError(err.message || "Couldn't join the waitlist right now — try again in a minute.");
    } finally {
      setBusy(false);
    }
  }

  const wide = useWide();
  if (wide) return <LandingWide f={{ email, setEmail, website, setWebsite, busy, done, resent, already, error, errorAt, join, emailRef, turnstileRef }} onHaveCode={onHaveCode} onSignIn={onSignIn} waitlistConfirmed={waitlistConfirmed} invite={invite} invited={invited} onWaitlist={openWaitlist} />;

  return (
    <div className="relative min-h-screen min-h-[100dvh] flex flex-col p-4 max-w-lg mx-auto lb-frame" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 16px)" }}>
      <div className="flex-1 flex items-center justify-center">
        {/* V.4 (Alan): the landing page sits in the same framed column as the app on wide
            screens, and its content column widens to 560px there. */}
        <div className="w-full max-w-sm md:max-w-[560px]">
          {waitlistConfirmed && (
            <div className="border-t border-b border-slate-200 text-slate-700 text-xs font-semibold py-2.5 mb-3 text-center" data-testid="waitlist-confirmed-banner">
              {ct("landing.waitlist.confirmed_banner", "You're on the list. We'll email your invite code when the next wave opens.")}
            </div>
          )}
          <div className="bg-white rounded-lg border border-slate-200 p-6">
            <div className="mb-3 flex justify-center"><LittleBankersLogo /></div>
            <div className="text-center mb-1">
              <span className="text-xs font-bold bg-amber-100 text-amber-700 rounded px-2 py-0.5" data-testid="beta-pill">
                {ct("landing.beta_pill", "Invite-only Preview")}
              </span>
            </div>
            <h1 className="text-xl font-extrabold text-slate-900 text-center mb-1">
              {ct("landing.hero.heading", "Where kids learn the value of money")}
            </h1>
            <p className="text-sm text-slate-500 text-center mb-2">
              {ct("landing.hero.subheading", "Little Bankers brings helping out, learning, and saving together in one simple app for the whole family. Kids earn tokens, build towards goals, and watch their savings grow. You set what everything's worth and approve every request. We take care of the admin.")}
            </p>
            {/* Code-holders (this weekend: personally invited people) arrive already
                convinced — their route lives in the FIRST viewport so they never scroll
                past marketing meant for cold visitors. Same handler as the bottom CTA;
                distinct kind so the dashboard can see which entry point code-holders use. */}
            {/* LB-361: arriving on a shared link, the first screen holds their code and one
                Get started, in place of the "Got an invite code?" link. It stays after a
                failed attempt, so the error is shown where they tapped. */}
            {invite ? (
              <div className="mb-4"><InviteBox invite={invite} where="hero" onWaitlist={openWaitlist} /></div>
            ) : (
              <p className="text-center mb-4">
              <button
                className="text-xs font-bold text-slate-900 underline underline-offset-2"
                data-testid="landing-have-code-hero"
                onClick={() => { track("landing_cta", { kind: "have_code_hero" }); onHaveCode(); }}
              >
                {ct("landing.hero_have_code", "Got an invite code? Enter it here")}
              </button>
            </p>
            )}

            <div className="grid grid-cols-1 gap-2 mb-4">
              <Pillar icon="ic-medal" hue="gold" title={ct("landing.pillar.earn_title", "Earn")} body={ct("landing.pillar.earn_body", "Tokens for daily habits, jobs around the house and good behaviour — with streaks that keep it going.")} />
              <Pillar icon="ic-supersaver" hue="teal" title={ct("landing.pillar.save_title", "Save")} body={ct("landing.pillar.save_body", "Real goals, a Savings Vault, and Money Monday interest that shows saving pays.")} />
              <Pillar icon="ic-brain" hue="indigo" title={ct("landing.pillar.learn_title", "Learn")} body={ct("landing.pillar.learn_body", "Spelling, vocabulary, maths and times tables for ages 5 to 11, which adapt to your child's ability and are designed to challenge them.")} />
            </div>

            <PeekStrip />

            {/* Differentiation strip (review gap #1, wording finalised 27 Aug per P1 —
                no competitor named): positive-first claim in the brand-warm treatment,
                with the body now carrying the explicit "not a card, no real money
                stored" statement rather than leaving it to the support line. */}
            <div className="bg-white rounded-lg border-l-[3px] border-amber-400 px-4 py-3 mb-4" data-testid="youre-the-bank">
              <p className="text-sm font-bold text-amber-800 mb-0.5">
                {ct("landing.youre_the_bank_title", "You're the bank")}
              </p>
              <p className="text-xs text-amber-700 leading-relaxed">
                {ct("landing.youre_the_bank", "Little Bankers isn't a card or a bank account — no real money is stored anywhere. It's a family money tracker you run: you set what tokens are worth, and you approve every spend.")}
              </p>
            </div>

            {/* Pricing signal (review 1.6, wording per P2, 27 Aug): says both halves
                plainly — paid extras will come one day, a free tier is permanent — and
                promises nothing about specific beta-family terms. */}
            <div className="bg-white rounded-lg border-l-[3px] border-teal-500 px-4 py-2.5 mb-4" data-testid="free-banner">
              <p className="text-sm font-bold text-emerald-700">
                {ct("landing.free_banner", "All features are free during the Preview")}
              </p>
              <p className="text-xs text-emerald-600">
                {ct("landing.free_banner_sub", "All features, including premium ones, are free to use during the Preview. Don't worry, there will always be a fantastic free product for you to use.")}
              </p>
            </div>

            <MiniFaq />

            {/* LB-361: where the waitlist was, the same box. The "or / I've got a code" pair goes
                with it: this visitor has their code in front of them. */}
            {invited ? (
              <div className="mb-3"><InviteBox invite={invite} where="lower" onWaitlist={openWaitlist} /></div>
            ) : (
              <>
            {done ? (
              <div className="mb-3">
                <div className="border-t border-b border-slate-200 text-slate-600 text-xs font-medium py-2.5 mb-2 text-center" data-testid="waitlist-check-email">
                  {resent
                    ? ct("landing.waitlist.resent", "Sent again — give it a minute, and check your spam folder too.")
                    : ct("landing.waitlist.check_email", "One more step: we've sent you an email. Click the link in it to confirm your spot. If it hasn't arrived, check your spam folder.")}
                </div>
                <p className="text-xs text-slate-400 text-center mb-1.5">
                  {ct("landing.waitlist.cadence", "Waves open roughly fortnightly — your invite code lands by email when yours does.")}
                </p>
                {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mb-2">{error}</div>}
                <p className="text-center">
                  <button
                    disabled={busy}
                    className="text-xs font-bold text-slate-900 underline underline-offset-2 disabled:opacity-50"
                    data-testid="waitlist-resend"
                    onClick={() => join(true)}
                  >
                    {busy ? ct("landing.waitlist.resending", "Sending…") : ct("landing.waitlist.resend", "Didn't get the email? Send it again")}
                  </button>
                </p>
              </div>
            ) : (
              <div className="mb-3">
                <p className="text-xs text-slate-500 text-center mb-2">
                  {ct("landing.waitlist.pitch", "We're letting families in wave by wave — roughly fortnightly. Join the waitlist and your invite code lands by email.")}
                </p>
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  ref={emailRef}
                  data-testid="waitlist-email"
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-full mb-2"
                  placeholder={ct("landing.waitlist.email_placeholder", "Your email address")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && !busy && join(false)}
                />
                {/* Honeypot: visually hidden but present in the DOM; autocomplete=off so
                    browsers never fill it — only bots blindly completing every field do. */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
                <div ref={turnstileRef} className="flex justify-center" />
                {error && <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs font-semibold rounded-lg px-3 py-2 mb-2">{error}</div>}
                <button
                  disabled={busy || !emailOk}
                  className="w-full bg-slate-900 text-white font-bold text-sm rounded-lg py-2.5 disabled:opacity-50"
                  onClick={() => join(false)}
                >
                  {busy ? ct("landing.waitlist.joining", "Joining…") : ct("landing.waitlist.cta", "Join the waitlist")}
                </button>
                <p className="text-xs text-slate-400 text-center mt-2">
                  {ct("landing.waitlist.consent", "By joining, you're happy for us to email your invite code and occasional Preview updates. Unsubscribe anytime.")}
                </p>
              </div>
            )}

            <div className="flex items-center gap-3 mb-3" aria-hidden="true">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs font-medium text-slate-400">{ct("landing.divider", "or")}</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>
            <button
              className="w-full bg-white border border-slate-200 text-slate-700 font-bold text-sm rounded-xl py-2.5"
              data-testid="landing-have-code"
              onClick={() => { track("landing_cta", { kind: "have_code" }); onHaveCode(); }}
            >
              {ct("landing.have_code_cta", "I've got a code")}
            </button>
              </>
            )}
            {/* Already-a-member route (28 Aug, staging test C). A parent who reinstalls,
                clears their browser, or picks up a new phone arrives at this page with a
                household that already exists — and until now the only ways forward were
                the waitlist (wrong) and the invite gate (wrong, and their code is long
                since spent). This goes straight to the Join screen. It does NOT grant an
                invite pass: joining is gated on the household code AND passcode, which is
                stronger proof than an invite code, and the gate stays exactly where it is
                for anyone trying to CREATE a household. */}
            {onSignIn && (
              <p className="text-center mt-2.5">
                <button
                  className="text-xs font-bold text-slate-900 underline underline-offset-2"
                  data-testid="landing-sign-in"
                  onClick={() => { track("landing_cta", { kind: "sign_in" }); onSignIn(); }}
                >
                  {ct("landing.sign_in_cta", "Already have a household? Sign in with your code")}
                </button>
              </p>
            )}
          </div>

          {/* Trust block (review 1.3 + 5.4). Data-location wording deliberately SOFT
              ("hosted on Cloudflare") until DPIA D11 confirms whether KV/R2 are
              jurisdiction-pinned — hardening to "stored in the UK/EU" without that
              answer would put an unverified claim on the front door. */}
          <div className="text-center mt-3" data-testid="trust-block">
            <p className="text-xs text-slate-400 leading-relaxed">
              {ct("landing.trust.line1", "Designed and built in the UK by parents, for families to use. Your data is not sold or shared, no ads are shown to kids, and you are in total control.")}
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {" "}
              <a href="/privacy" className="underline font-semibold text-slate-500">{ct("landing.trust.privacy_link", "Read our privacy promise")}</a>
            </p>
            <p className="text-xs text-slate-400 mt-1">
              {ct("landing.trust.support", "Questions?")} <a href="mailto:support@littlebankers.uk" className="underline font-semibold text-slate-500">support@littlebankers.uk</a>
            </p>
          </div>
        </div>
      </div>
      <div className="text-center text-xs text-slate-400 pb-2">
        <a href="/terms" className="underline">Terms & Conditions</a>
        <span className="mx-1.5">·</span>
        <a href="/privacy" className="underline">Privacy Policy</a>
      </div>
      <CopyrightFooter />
      <SafeAreaSpacer />
    </div>
  );
}
